import ApolloClient from 'apollo-boost';
/**
 * We need to fetch Storyblok data on both environments node & browser.
 * cross-fetch allows us to replace default fetch with code that can be run on
 * desired env.
 */
import fetch from 'cross-fetch';
import qql from 'graphql-tag';

import { isCacheMode } from '../../services/isCacheMode';

import { Settings } from '../../providers';
import { addStoryblokData } from '../../state';
import { Storyblok } from '..';

export class StoryblokClient {
    /**
     * Apollo client with predefined storyblok settings allows us to
     * do a GraphQL query to retrieve storyblok data.
     */
    protected client: ApolloClient<unknown>;
    protected version: Settings['languageVersion'];
    protected dispatch?: any;

    constructor(version: Settings['languageVersion'], dispatch?: any) {
        this.client = new ApolloClient({
            uri: 'https://gapi.storyblok.com/v1/api',
            fetch,
            request: (operation) => {
                operation.setContext({
                    headers: {
                        token: 'Q4ENwK9j26wzITwKogG6ewtt',
                        version: 'published',
                    },
                });
            },
        });

        this.version = version;
        this.dispatch = dispatch;

        if (isCacheMode())
            console.log(`Application is using cached API content ✍`);
    }

    protected queryStoryblokData = (): string => {
        const contentQueries: {
            [Key in Storyblok.Content.ContentType]: string;
        } = {
            advantages: `
                AdvantageItems(starts_with: "${this.version}/") {
                    items {
                        slug
                        content {
                            text
                            informations
                        }
                    }
                }
            `,
            articles: `
                ArticleItems(sort_by: "first_published_at:desc", starts_with: "${this.version}/") {
                    items {
                        id
                        slug
                        first_published_at
                        published_at
                        content {
                            content
                            excerpt
                            title
                            author
                            featuredImage {
                                id
                                filename
                                alt
                            }
                            site_title
                            site_description
                        }
                    }
                }
            `,
            errors: `
                ErrorItems(starts_with: "${this.version}/") {
                    items {
                        slug
                        content {
                            message
                            button_label
                            button_event
                            redirect_url
                        }
                    }
                }
            `,
            factorGroups: `
                FactorgroupItems(starts_with: "${this.version}/") {
                    items {
                        slug
                        content {
                            _uid
                            heading
                            buttonLabelPositive
                            buttonLabelNegative
                            factorItems
                        }
                    }
                }
            `,
            footer: `
                FooterItem(id: "${this.version}/global/footer") {
                    content {
                        renderSettings
                        components
                    }
                }
            `,
            header: `
                HeaderItem(id: "${this.version}/global/header") {
                    content {
                        logo
                        mobile_cta_label
                        mobile_cta_url
                        navigationMenu
                    }
                }
            `,
            middlesteps: `
                MiddlestepItems(starts_with: "${this.version}/") {
                    items {
                        slug
                        content {
                            text
                            primary_offers_button_label
                            secondary_offers_button_label
                        }
                    }
                }
            `,
            offers: `
                OfferItems(starts_with: "${this.version}/") {
                    items {
                        slug
                        content {
                            logo
                            logoPadding
                            trustboxCode
                            content
                            contact_form_enabled
                            contactFormFields
                            contactFormButtonLabel
                            contact_form_submitted_message
                            calculator_enabled
                            calculator_components
                        }
                    }
                }
            `,
            pages: `
                PageItems(starts_with: "${this.version}/pages/", per_page: 100) {
                    items {
                        slug
                        is_startpage
                        full_slug
                        content {
                            _uid
                            sections
                            space_from_header
                            white_header_desktop
                            hide_footer
                            site_title
                            site_description
                            page_layout
                            calculateMinHeight
                            pageGuard
                        }
                    }
                }
            `,
            strings: `
                StringsItems(starts_with: "${this.version}/") {
                    items {
                        slug
                        content {
                            strings
                        }
                    }
                }
            `,
        };

        /**
         * The query needs to be wrapped with `{}`
         * Join with empty string because graphQL does not accept any chars
         * between queries.
         */
        return `{${Object.values(contentQueries).join('')}}`;
    };

    resolveCachePath = () => import(`../../cache/data-${this.version}.json`);

    /**
     * Makes a graphQL query to Storyblok Client with predefined schema name.
     * Returns object whicd ch contains query reponse.data.
     *
     * You can pass `true` as second argument to add fetched content into
     * Redux store's state.
     */
    getStoryblokContent = () =>
        isCacheMode()
            ? this.resolveCachePath().then((data) => {
                  if (typeof window !== 'undefined') {
                      this.dispatch(addStoryblokData(data));
                  }

                  return data;
              })
            : this.client
                  .query({
                      query: qql`${this.queryStoryblokData()}`,
                  })
                  .then((response) => {
                      if (typeof window !== 'undefined') {
                          this.dispatch(addStoryblokData(response.data));
                      }

                      return response.data;
                  });
}
